import { computed, ref } from 'vue';

import { defineStore } from 'pinia';

import { Result } from '@/classes/result';
import { useAuthStore } from '@/stores/auth';
import type { NewNotification, ShortenedNotification } from '@/types/notification';
import { callApi } from '@/utils/api';
import { handleAxiosError } from '@/utils/error';

export const useNotificationStore = defineStore('notifications', () => {
    const employeeHasNotifications = computed(() => (employeeNotifications.value ? employeeNotifications.value.length > 0 : false));
    const employeeNotifications = ref<ShortenedNotification[]>();
    const amountOfUnreadNotifications = computed(
        () => employeeNotifications.value?.filter((notification: ShortenedNotification) => !notification.isRead).length
    );

    async function getEmployeeNotifications() {
        return employeeNotifications.value;
    }

    async function getForLoggedInEmployee(): Promise<ShortenedNotification[]> {
        try {
            const authStore = useAuthStore();

            const result = await callApi({
                method: 'GET',
                endpoint: `notifications/${authStore.getLoggedInEmployee()._id}`
            });

            const notifications = result.object as ShortenedNotification[];
            employeeNotifications.value = notifications;

            return notifications;
        } catch (error) {
            return handleAxiosError(error, [], 'Notificaties konden niet worden opgehaald');
        }
    }

    async function create(notification: NewNotification): Promise<Result> {
        try {
            const result = await callApi({
                method: 'POST',
                endpoint: 'notifications',
                body: notification
            });

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Notificatie kon niet aangemaakt worden');
        }
    }

    async function setRead(id: string) {
        try {
            const authStore = useAuthStore();
            const result = await callApi({
                method: 'PUT',
                endpoint: `notifications/${id}/${authStore.getLoggedInEmployee()._id}/read`
            });

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Notificatie kon niet op gelezen worden gezet');
        }
    }

    async function setUnread(id: string) {
        try {
            const authStore = useAuthStore();
            const result = await callApi({
                method: 'PUT',
                endpoint: `notifications/${id}/${authStore.getLoggedInEmployee()._id}/unread`
            });

            return result;
        } catch (error) {
            return handleAxiosError(error, [], 'Notificatie kon niet op ongelezen worden gezet');
        }
    }

    return {
        getForLoggedInEmployee,
        create,
        setRead,
        setUnread,
        amountOfUnreadNotifications,
        employeeHasNotifications,
        getEmployeeNotifications,
        employeeNotifications
    };
});
